/**
 * Brand presets — runtime-managed platform-wide starter templates.
 *
 * The frontend MERGES two sources:
 *   1. Hardcoded presets in `frontend/src/brand/presets/*.ts` (m2, brij) —
 *      shipped with the build, always available.
 *   2. DB-backed presets created from the UI via these endpoints — listed
 *      first in the gallery, can be edited/deleted by super-admin.
 *
 * On id collisions, DB rows take precedence (since they're newer / authored).
 */

import api from '@/services/api';
import type { BrandOverrides } from '@/brand/types';

export interface BrandPresetRecord {
  preset_id: string;
  preset_name: string;
  preset_description: string | null;
  brand_overrides: BrandOverrides;
  is_system: boolean;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreatePresetPayload {
  preset_name: string;
  preset_description?: string;
  brand_overrides: BrandOverrides;
}

export interface UpdatePresetPayload {
  preset_name?: string;
  preset_description?: string | null;
  brand_overrides?: BrandOverrides;
}

const BASE = '/super-admin/brand-presets';

export async function listPresets(query?: string): Promise<BrandPresetRecord[]> {
  const res = await api.get(BASE, { params: query ? { q: query } : undefined });
  return res.data?.presets || [];
}

export async function getPreset(presetId: string): Promise<BrandPresetRecord | null> {
  try {
    const res = await api.get(`${BASE}/${presetId}`);
    return res.data?.preset || null;
  } catch {
    return null;
  }
}

export async function createPreset(payload: CreatePresetPayload): Promise<BrandPresetRecord> {
  const res = await api.post(BASE, payload);
  return res.data.preset;
}

export async function updatePreset(presetId: string, payload: UpdatePresetPayload): Promise<BrandPresetRecord> {
  const res = await api.patch(`${BASE}/${presetId}`, payload);
  return res.data.preset;
}

export async function deletePreset(presetId: string): Promise<void> {
  await api.delete(`${BASE}/${presetId}`);
}
